import {
  alterDerAufgabe,
  nurVorraete,
  TASK_STATUS_LABELS,
  type TaskSource,
  type TaskStatus,
  MARKIER_FILTER,
  type MarkierFilter,
} from '@netcon-work/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { AufgabenVerweis } from '../components/AufgabeKontext'
import { Topbar } from '../components/Topbar'
import { Button, Card, CardPad, PrioBadge, SourceBadge } from '../components/ui/primitives'
import { poolsQuery, type Pool } from '../lib/queries'
import { trpc } from '../lib/trpc'
import { useMerker } from '../lib/merker'
import { MarkierFilterLeiste, SonnenMarke, useMarkierFilter } from '../components/Markierung'
import { nebenBestandVerwerfen } from '../lib/mutationen'

/**
 * Pools-Ansicht (`v-pools`, Konzept Kap. 4.2, Phase 4 AP 4.3).
 *
 * ## Was hier eine Aussage ist und was ein Fehler
 *
 * **Ein Pool über seinem Limit wird gewarnt, nicht gesperrt.** Ein übervoller
 * „Sofort"-Pool sagt etwas über die Lage – wer das Betanken hart abweist, bringt
 * niemanden dazu, weniger Arbeit zu haben, sondern dazu, den Pool zu umgehen.
 *
 * **Pools sind Vorräte, keine Prio-Stufen.** Die Priorität steht als Abzeichen an
 * der Aufgabe und sortiert *innerhalb* des Pools. Deshalb tragen die Pools hier
 * keine Ampelfarben nach Dringlichkeit – sie haben Themenfarben aus dem Mockup.
 *
 * **Mehrfach-Zugehörigkeit wird benannt.** Steht dieselbe Aufgabe in zwei Pools,
 * sagt ein Hinweis das an beiden Stellen. Ohne ihn zählt man sie zweimal.
 *
 * **Diese Ansicht bleibt lesend, und das ist kein Rückstand.** Betankt wird über die
 * Pool-Auswahl in „Alle Aufgaben" (AP 4.3), gezogen wird in „Mein Tag" (AP 4.4) – dort,
 * wo die Aufgaben stehen, die man auswählt. Offen ist nur Drag & Drop als *zusätzlicher*
 * Weg; die Auswahl kam zuerst, weil sie mit der Tastatur funktioniert (Leitplanke 3).
 */
export function Pools() {
  /**
   * **Das Detail auf einen Klick** (03.08.2026). Nils: *„Sonst kann ich den Inhalt nie
   * beurteilen."* Wer aus einem Pool zieht, will vorher wissen, was drinsteht – und dafür
   * die Ansicht zu wechseln, macht aus einer Entscheidung eine Recherche.
   */
  const { data: pools = [], isLoading } = useQuery(poolsQuery)
  /**
   * **Liste oder Spalten** (Nils, 17.08.2026: *„optional als Liste anzeigbar"*).
   *
   * Drei Spalten nebeneinander beantworten *„wie ist es verteilt"*. Eine Liste beantwortet
   * *„was liegt am längsten"* – dafür müssen alle Aufgaben in **einer** Sortierung stehen,
   * und quer über drei Spalten kann man nicht sortieren.
   */
  const [alsListe, setAlsListe] = useState(false)
  /** „Mein Tag"-Filter (Z-20) – dieselbe Reihe wie in „Alle Aufgaben", gemerkt je Gerät. */
  const [meinTag, setMeinTag] = useMerker<MarkierFilter | 'alle'>('pools-meintag', 'alle', [
    'alle',
    ...MARKIER_FILTER,
  ])
  const passt = useMarkierFilter()

  /**
   * **Vorräte oben, Regale unten** (20.08.2026, [E-191](../../../docs/ENTSCHEIDUNGEN.md)).
   *
   * Der Parkplatz steht **nicht** als vierte Spalte neben den drei Vorräten. Er beantwortet
   * eine andere Frage: die Spalten sagen *„was kann ich mir holen"*, das Regal sagt *„was habe
   * ich weggelegt"*. Nebeneinander gestellt sähe es aus wie ein vierter Vorrat, und die
   * Verteilung – der ganze Zweck der Nebeneinanderstellung – wäre um einen Balken verzerrt,
   * der keine Arbeit ist.
   */
  /**
   * **Gefiltert wird die Liste, nicht der Pool.** Zahlen, Limits und Alter im Kopf jeder Spalte
   * bleiben die des ganzen Vorrats – ein Filter, der „3 / 20" zu „1 / 20" machte, hieße den
   * Füllstand nach der eigenen Absicht zu messen.
   */
  const alleVorraete = nurVorraete(pools)
  const vorraete =
    meinTag === 'alle'
      ? alleVorraete
      : alleVorraete.map((p) => ({
          ...p,
          aufgaben: p.aufgaben.filter((a) => passt(a.id, meinTag)),
        }))
  const regale = pools.filter((p) => !p.ziehbar)

  const gesamt = alleVorraete.reduce((s, p) => s + p.aufgaben.length, 0)

  return (
    <>
      <Topbar
        titel="Pools"
        untertitel="Thematische Vorräte – gezogen wird daraus, nicht zugewiesen"
      />

      {isLoading && <div className="text-[13px] text-ink2">Lädt …</div>}

      {!isLoading && gesamt === 0 && (
        <Card className="mb-5">
          <CardPad>
            <div className="text-[13px] font-semibold">Die Pools sind noch leer.</div>
            <p className="mt-1.5 text-xs text-ink2">
              Betankt wird in „Alle Aufgaben" über die Pool-Auswahl – auch importierte Aufgaben.
              Gezogen wird daraus in „Mein Tag". Dass hier nichts liegt, ist kein Fehlstand: in
              einen Pool kommt, was jemand als Vorrat zusagt, nicht alles, was importiert wurde.
            </p>
          </CardPad>
        </Card>
      )}

      {gesamt > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-[11.5px]">
          <button
            type="button"
            onClick={() => setAlsListe((v) => !v)}
            className="cursor-pointer rounded-lg border border-line2 px-2.5 py-1 text-ink2 hover:bg-[#f3f2ef]"
          >
            {alsListe ? 'als Spalten' : 'als Liste'}
          </button>
          <span className="text-muted">
            {alsListe
              ? 'Je Pool eine Liste, wichtigste oben, darin älteste zuerst'
              : 'Nebeneinander, wie im Mockup – so sieht man die Verteilung'}
          </span>
          <span className="ml-auto">
            <MarkierFilterLeiste
              taskIds={alleVorraete.flatMap((p) => p.aufgaben.map((a) => a.id))}
              wert={meinTag}
              setzen={setMeinTag}
            />
          </span>
        </div>
      )}

      {alsListe ? (
        <PoolListe pools={vorraete} />
      ) : (
        <div className="grid grid-cols-1 items-start gap-[18px] md:grid-cols-2 lg:grid-cols-3">
          {vorraete.map((pool) => (
            <PoolSpalte key={pool.id} pool={pool} />
          ))}
        </div>
      )}

      {regale.map((regal) => (
        <Regal key={regal.id} pool={regal} />
      ))}
    </>
  )
}

/**
 * **Das Regal** – der Parkplatz und alles, was ihm später gleicht (20.08.2026,
 * [E-191](../../../docs/ENTSCHEIDUNGEN.md)).
 *
 * Nils: *„Das sind manchmal so elendig alte Dinger, die aber nur als Info drin sind."*
 *
 * ## Drei Dinge fehlen hier bewusst
 *
 * - **Kein Alters-Alarm.** Die Karten der Vorräte färben ab 90 Tagen rot, weil ein Vorrat,
 *   der altert, ein Rückstand ist. Ein Regal altert von Berufs wegen: was hier liegt, ist
 *   *deshalb* hier, weil es niemand macht. Rot wäre eine Warnung, die man wegsieht – und die
 *   nächste echte gleich mit.
 * - **Kein Limit und kein Füllstand.** Es gibt nichts abzuarbeiten.
 * - **Kein „Ziehen".** Statt dessen der eine Weg heraus: zurück in den Eingang, wo die
 *   Aufgabe wieder beurteilt wird.
 *
 * Eingeklappt, solange niemand hinsieht: das Regal ist die Ansicht, die man **sucht**, nicht
 * die, die man liest. Aufgeklappt stünde es unter drei Vorräten mit möglicherweise
 * hundertfachem Inhalt und schöbe sie aus dem Bild.
 */
function Regal({ pool }: { pool: Pool }) {
  const queryClient = useQueryClient()
  const [offen, setOffen] = useState(false)

  const zurueck = useMutation({
    mutationFn: (taskId: string) => trpc.eingang.zurueckInDenEingang.mutate({ taskIds: [taskId] }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['pools'] })
      await nebenBestandVerwerfen(queryClient)
      await queryClient.invalidateQueries({ queryKey: ['eingang'] })
    },
  })

  const anzahl = pool.aufgaben.length

  return (
    <Card className="mt-4">
      <CardPad>
        <button
          type="button"
          onClick={() => setOffen((v) => !v)}
          aria-expanded={offen}
          className="flex w-full cursor-pointer items-baseline gap-2 text-left"
        >
          <span className="text-base">{pool.icon}</span>
          <b className="text-[13.5px]">{pool.label}</b>
          <span className="tabular text-[11.5px] text-muted">
            {anzahl === 0 ? 'leer' : `${anzahl} geparkt`}
          </span>
          <span className="ml-auto text-[11.5px] text-accent">
            {offen ? 'einklappen' : 'ansehen'}
          </span>
        </button>

        {pool.regeln && <p className="mt-1 text-[11.5px] text-muted">{pool.regeln}</p>}

        {zurueck.error && (
          <p role="alert" className="mt-2 text-[11.5px] text-pool-sofort">
            {zurueck.error.message}
          </p>
        )}

        {offen && anzahl === 0 && (
          <p className="mt-2 text-[12.5px] text-muted">
            Nichts geparkt. Im Eingang legt „Parken" etwas hierher.
          </p>
        )}

        {offen &&
          pool.aufgaben.map((aufgabe) => (
            <div
              key={aufgabe.id}
              className="flex flex-wrap items-center gap-2 border-b border-line py-1.5 text-[12.5px] last:border-b-0"
            >
              <SourceBadge source={aufgabe.sourceId as TaskSource} />
              <AufgabenVerweis
                aufgabeId={aufgabe.id}
                className="min-w-0 flex-1 cursor-pointer truncate text-left hover:underline"
                title={aufgabe.title}
              >
                {aufgabe.title}
              </AufgabenVerweis>
              {/**
               * **Wann jemand sie weggelegt hat** – nicht, wie alt sie ist. Das Alter ist hier
               * oft dreistellig und sagt nichts; das Ablagedatum sagt, ob man dieses Stück
               * schon einmal in der Hand hatte.
               */}
              <span className="w-[86px] shrink-0 text-right text-[11px] text-muted">
                {new Date(aufgabe.addedAt).toLocaleDateString('de-DE')}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={zurueck.isPending}
                onClick={() => zurueck.mutate(aufgabe.id)}
                title="Holt die Aufgabe zurück in den Eingang – mit Beschreibung, Anhängen und Verlauf"
              >
                zurück in den Eingang
              </Button>
            </div>
          ))}
      </CardPad>
    </Card>
  )
}

/**
 * **Das Alter des Vorrats** (Nils, 17.08.2026: *„Da sollte auch noch das Alter mit
 * auftauchen."*).
 *
 * Angegeben wird das **älteste** Stück, nicht der Durchschnitt. Ein Mittelwert über einen
 * Vorrat, in den ständig Neues kommt, sieht immer gesund aus – die Frage lautet aber nicht
 * *„wie alt ist der Pool im Schnitt"*, sondern *„was liegt hier am längsten und warum"*.
 */
function aeltestesInTagen(pool: Pool): number | null {
  if (pool.aufgaben.length === 0) return null
  return Math.max(...pool.aufgaben.map((a) => alterDerAufgabe(a)))
}

/** Ab 90 Tagen rot, ab 30 gelb – dieselben Stufen wie bei den wartenden Abnahmen. */
function altersFarbe(tage: number): string {
  return tage >= 90 ? 'text-pool-sofort' : tage >= 30 ? 'text-[#8a6100]' : 'text-muted'
}

function PoolSpalte({ pool }: { pool: Pool }) {
  const anzahl = pool.aufgaben.length
  const aeltestes = aeltestesInTagen(pool)

  return (
    <Card>
      <CardPad>
        <div className="flex items-baseline gap-2">
          <span className="text-base">{pool.icon}</span>
          <b className="text-[13.5px]">{pool.label}</b>
          <span className="tabular ml-auto text-[11.5px] text-muted">
            {pool.limitAnzahl === null ? anzahl : `${anzahl} / ${pool.limitAnzahl}`}
          </span>
        </div>

        {aeltestes !== null && (
          <div className={`mt-1 text-[11.5px] ${altersFarbe(aeltestes)}`}>
            ältestes Stück: <strong>{aeltestes} Tage</strong>
          </div>
        )}

        {pool.regeln && <p className="mt-1.5 text-[11.5px] text-muted">{pool.regeln}</p>}

        {/* Füllstand. Spur ist ein hellerer Schritt derselben Farbe, nicht Grau. */}
        {pool.limitAnzahl !== null && (
          <div className="mt-2 h-[5px] overflow-hidden rounded-full bg-accent-soft">
            <i
              className={`block h-full rounded-full ${
                pool.ueberLimit ? 'bg-pool-sofort' : 'bg-accent'
              }`}
              style={{ width: `${Math.min(100, (anzahl / pool.limitAnzahl) * 100)}%` }}
            />
          </div>
        )}

        {pool.ueberLimit && (
          <p className="mt-2 rounded-lg bg-[#fdf3dc] px-2.5 py-1.5 text-[11.5px] text-[#8a6100]">
            ⚠ Über dem Limit ({anzahl} von {pool.limitAnzahl}). Das ist eine Aussage über die Lage,
            keine Sperre – betanken bleibt möglich.
          </p>
        )}

        {pool.wipDurchbrechen && (
          <p className="mt-2 text-[11px] font-semibold text-pool-sofort">
            Darf das persönliche WIP-Limit durchbrechen
          </p>
        )}

        <div className="mt-3">
          {anzahl === 0 && <p className="py-2 text-[12.5px] text-muted">Nichts im Vorrat.</p>}

          {pool.aufgaben.map((aufgabe) => (
            <div
              key={aufgabe.id}
              className="border-b border-line py-2 text-[12.5px] last:border-b-0"
            >
              <div className="flex flex-wrap items-center gap-1.5">
                {aufgabe.priority && <PrioBadge priority={aufgabe.priority} />}
                <SourceBadge source={aufgabe.sourceId as TaskSource} />
                <SonnenMarke taskId={aufgabe.id} klein />
                {aufgabe.inPools > 1 && (
                  <span
                    className="rounded-md bg-[#f3f2ef] px-[7px] py-[2.5px] text-[10px] font-semibold text-ink2"
                    title="Diese Aufgabe liegt in mehreren Pools – gezogen wird sie einmal und verschwindet dann aus allen."
                  >
                    in {aufgabe.inPools} Pools
                  </span>
                )}
              </div>
              <AufgabenVerweis
                aufgabeId={aufgabe.id}
                className="mt-1 block text-left font-semibold hover:text-accent hover:underline"
              >
                {aufgabe.title}
              </AufgabenVerweis>
              <div className="mt-0.5 text-[11px] text-muted">
                {TASK_STATUS_LABELS[aufgabe.status as TaskStatus] ?? aufgabe.status}
                {aufgabe.assigneeName && ` · ${aufgabe.assigneeName}`}
              </div>
            </div>
          ))}
        </div>
      </CardPad>
    </Card>
  )
}

/**
 * **Je Pool eine Liste, wichtigste oben** (Nils, 18.08.2026: *„Wenn man auf «als Liste»
 * drückt, dann auch gruppiert – also pro Pool die wichtigsten oben."*).
 *
 * Der erste Anlauf warf alles in **eine** Liste, nach Alter sortiert. Das beantwortete
 * *„was liegt am längsten"* – aber die Frage davor lautet *„in welchem Pool"*, und ohne
 * diese Trennung steht eine Sofort-Aufgabe von gestern unter dreißig alten aus dem
 * Nachschub.
 *
 * Innerhalb eines Pools weiter **älteste zuerst**: dort ist die Frage wieder die nach dem
 * Rückstand. Die Reihenfolge der Pools kommt aus `sort_order` – dieselbe Wichtigkeit, die
 * auch der Trichter benutzt.
 *
 * Eine Aufgabe in zwei Pools steht in **beiden** Listen, mit einem Hinweis. Anders als in
 * der zusammengeworfenen Liste ist das hier richtig: die Frage lautet „was liegt in diesem
 * Pool", und dort liegt sie.
 */
function PoolListe({ pools }: { pools: Pool[] }) {
  const mitInhalt = pools.filter((p) => p.aufgaben.length > 0)
  if (mitInhalt.length === 0) return null

  return (
    <div className="grid gap-3">
      {mitInhalt.map((pool) => {
        const zeilen = [...pool.aufgaben].sort((a, b) => alterDerAufgabe(b) - alterDerAufgabe(a))
        const aeltestes = aeltestesInTagen(pool)
        return (
          <Card key={pool.id}>
            <CardPad>
              <div className="mb-1.5 flex flex-wrap items-baseline gap-2 border-b border-line pb-1.5">
                <span className="text-base">{pool.icon}</span>
                <b className="text-[13.5px]">{pool.label}</b>
                <span className="tabular text-[11.5px] text-muted">
                  {pool.aufgaben.length}
                  {pool.limitAnzahl === null ? '' : ` / ${pool.limitAnzahl}`}
                </span>
                {aeltestes !== null && (
                  <span className={`text-[11.5px] ${altersFarbe(aeltestes)}`}>
                    ältestes {aeltestes} Tage
                  </span>
                )}
                {pool.ueberLimit && (
                  <span className="text-[11.5px] font-semibold text-[#8a6100]">über dem Limit</span>
                )}
              </div>

              {zeilen.map((aufgabe) => {
                const tage = alterDerAufgabe(aufgabe)
                return (
                  <div
                    key={aufgabe.id}
                    className="flex flex-wrap items-center gap-2 border-b border-line py-1.5 text-[12.5px] last:border-b-0"
                  >
                    <span
                      className={`tabular w-[62px] shrink-0 font-semibold ${altersFarbe(tage)}`}
                    >
                      {tage} T
                    </span>
                    {aufgabe.priority && <PrioBadge priority={aufgabe.priority} />}
                    <SourceBadge source={aufgabe.sourceId as TaskSource} />
                    <AufgabenVerweis
                      aufgabeId={aufgabe.id}
                      className="min-w-0 flex-1 cursor-pointer truncate text-left hover:underline"
                      title={aufgabe.title}
                    >
                      {aufgabe.title}
                    </AufgabenVerweis>
                    <SonnenMarke taskId={aufgabe.id} klein />
                    {aufgabe.inPools > 1 && (
                      <span
                        className="shrink-0 rounded-md bg-[#f3f2ef] px-[7px] py-[2.5px] text-[10px] font-semibold text-ink2"
                        title="Liegt in mehreren Pools – gezogen wird sie einmal und verschwindet dann aus allen."
                      >
                        in {aufgabe.inPools} Pools
                      </span>
                    )}
                    <span className="w-[92px] shrink-0 text-right text-[11px] text-muted">
                      {TASK_STATUS_LABELS[aufgabe.status as TaskStatus] ?? aufgabe.status}
                    </span>
                  </div>
                )
              })}
            </CardPad>
          </Card>
        )
      })}
    </div>
  )
}
